export type Verdict = 'BUY' | 'ACCEPTABLE' | 'WAIT';
export type Sentiment = 'positive' | 'negative' | 'neutral';
export type CallTone = 'positive' | 'neutral' | 'cautious' | 'negative';

export interface LatestTranscript {
  period: string;
  year: number;
  quarter: number;
  callDate: string;
  fetchedAt: string;
  headline: string;
  tone: CallTone;
  oneLiner: string;
  guidanceChanges?: string;
  capitalAllocation?: string;
  analystPushback?: string;
  keyQuotes?: Array<{ speaker: string; quote: string; context?: string }>;
  risksFlagged?: string;
  fullText: string;
}

export interface CongressionalTradeRow {
  transactionDate: string;
  disclosureDate: string;
  legislator: string;
  chamber: 'Senate' | 'House';
  district: string;
  owner: string;
  type: string;
  amount: string;
  link: string;
}
export interface CongressionalActivity {
  fetchedAt: string;
  windowDays: number;
  totalLast90Days: number;
  buyCount: number;
  sellCount: number;
  trades: CongressionalTradeRow[];
}

export interface EarningsMomentum {
  epsRevision: number | null;
  earningsSurprisePct: number | null;
  signal: 'pos' | 'neg' | 'flat' | 'none';
  revisionDelta7d: number | null;
  revisionDelta30d: number | null;
  revisionDelta90d: number | null;
  revisionAcceleration: number | null;
}
export interface CompositeVelocity {
  d1: number | null;
  d5: number | null;
  d20: number | null;
  d20Rank: number | null;
  d20PctRank: number | null;
  universeCount: number;
}

export interface SmartEntry { verdict: Verdict; priceAnchor: number | null; reasons: string[] }
export interface EventRow { ts: string; type: string; headline: string; sourceUrl?: string; sentiment: Sentiment }
export interface StoryResp {
  symbol: string;
  asOf: string;
  currentPrice: number;
  entry: { date: string; price: number } | null;
  smartEntry: SmartEntry;
  model: {
    q?: number; v?: number; composite?: number; rsi?: number | null;
    mtdPct?: number | null; ytdPct?: number | null; oneWeekPct?: number | null;
    high52w?: number | null; sector?: string; industry?: string;
  } | null;
  fairValue: {
    today?: number; dcf?: number; bull?: number; bear?: number;
    threeYearTarget?: number; fiveYearTarget?: number;
    asymmetry?: number; upside?: number;
  } | null;
  consensus: {
    targetPrice?: number; rating?: number; buy?: number; hold?: number; sell?: number;
    totalAnalysts?: number;
  } | null;
  lastEarnings: {
    date?: string; period?: string; epsActual?: number; epsEstimate?: number;
    surprisePct?: number; yoyGrowthPct?: number | null;
  } | null;
  events: EventRow[];
  summary: { headline: string; risks: string[]; positives: string[]; decision: string };
  latestTranscript: LatestTranscript | null;
  congressionalActivity: CongressionalActivity | null;
  earningsMomentum: EarningsMomentum | null;
  compositeVelocity: CompositeVelocity | null;
}

export const TONE_STYLE: Record<CallTone, { pill: string; label: string }> = {
  positive: { pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40', label: 'POSITIVE TONE' },
  neutral:  { pill: 'bg-zinc-500/15  text-zinc-300  border-zinc-400/40',  label: 'NEUTRAL TONE'  },
  cautious: { pill: 'bg-amber-500/15  text-amber-300  border-amber-400/40',  label: 'CAUTIOUS TONE' },
  negative: { pill: 'bg-red-500/15    text-red-300    border-red-400/40',    label: 'NEGATIVE TONE' },
};

export const VERDICT_TONE: Record<Verdict, { bg: string; border: string; text: string; label: string }> = {
  BUY:        { bg: 'bg-emerald-500/15', border: 'border-emerald-400/60', text: 'text-emerald-300', label: 'BUY — within fair-value entry zone' },
  ACCEPTABLE: { bg: 'bg-amber-500/15',   border: 'border-amber-400/60',   text: 'text-amber-300',   label: 'ACCEPTABLE — entry OK but watch signals' },
  WAIT:       { bg: 'bg-red-500/15',     border: 'border-red-400/60',     text: 'text-red-300',     label: 'WAIT — defer entry, signals against' },
};

export const SENTIMENT_DOT: Record<Sentiment, string> = {
  positive: 'bg-emerald-400',
  negative: 'bg-red-400',
  neutral:  'bg-zinc-500/60',
};

export function fmtPct(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}
export function fmtMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}
export function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  const ageH = (Date.now() - t) / 3_600_000;
  if (ageH < 1) return `${Math.round(ageH * 60)}m ago`;
  if (ageH < 24) return `${ageH.toFixed(0)}h ago`;
  return `${Math.round(ageH / 24)}d ago`;
}
